/**
 * useTemaLogin — Configuración remota de la pantalla de Login.
 *
 * Permite cambiar la imagen, el lema, el color y un aviso del login sin
 * recompilar el APK. El backend (n8n) decide QUÉ tema está vigente hoy, para
 * no depender del reloj del celular (muchos equipos lo tienen mal).
 *
 * Reglas de robustez (el login NUNCA debe quedarse en blanco):
 *   1. Si no hay URL configurada  → se usa el diseño por defecto del APK.
 *   2. Si no hay internet         → se usa el último tema guardado en caché.
 *   3. Si el servidor tarda       → se corta a los 3 s y sigue con lo que haya.
 *   4. Si la imagen no carga      → Login.tsx vuelve a la ilustración incluida.
 */
import { useEffect, useState } from 'react';

export type TemaLogin = {
  tema?: string;    // nombre del tema, solo informativo (ej. "navidad")
  imagen?: string;  // URL https de la ilustración
  lema?: string;    // texto bajo la ilustración
  color?: string;   // color de marca en formato #rrggbb
  aviso?: string;   // mensaje temporal (mantenimiento, promoción…)
};

const CLAVE_CACHE = 'tema_login_cache';
const ESPERA_MAXIMA_MS = 3000;

/** Lema que trae el APK cuando no hay ningún tema configurado. */
export const LEMA_POR_DEFECTO = 'Conectando hogares, uniendo familias';

function leerCache(): TemaLogin {
  try {
    const guardado = localStorage.getItem(CLAVE_CACHE);
    return guardado ? JSON.parse(guardado) : {};
  } catch {
    return {};
  }
}

/**
 * Valida lo que llega del backend. Aunque el servidor sea nuestro, la respuesta
 * se trata como dato externo: solo se aceptan imágenes por HTTPS y colores con
 * formato correcto, y los textos se recortan para que no rompan el diseño.
 */
function sanear(dato: any): TemaLogin {
  const tema: TemaLogin = {};
  if (typeof dato?.tema === 'string' && dato.tema.trim()) {
    tema.tema = dato.tema.trim().slice(0, 60);
  }
  if (typeof dato?.imagen === 'string' && /^https:\/\/\S+$/i.test(dato.imagen.trim())) {
    tema.imagen = dato.imagen.trim();
  }
  if (typeof dato?.lema === 'string' && dato.lema.trim()) {
    tema.lema = dato.lema.trim().slice(0, 90);
  }
  if (typeof dato?.color === 'string' && /^#[0-9a-f]{6}$/i.test(dato.color.trim())) {
    tema.color = dato.color.trim();
  }
  if (typeof dato?.aviso === 'string' && dato.aviso.trim()) {
    tema.aviso = dato.aviso.trim().slice(0, 180);
  }
  return tema;
}

export function useTemaLogin(): TemaLogin {
  // Arranca con el último tema conocido para que se vea bien al instante,
  // incluso sin conexión. El inicializador es puro (sin efectos secundarios).
  const [tema, setTema] = useState<TemaLogin>(leerCache);

  useEffect(() => {
    const url = process.env.REACT_APP_N8N_URL_TEMAS;
    if (!url) return; // sin backend configurado: se queda el diseño por defecto

    const control = new AbortController();
    const corte = setTimeout(() => control.abort(), ESPERA_MAXIMA_MS);

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accion: 'activo' }),
      signal: control.signal,
    })
      .then(r => (r.ok ? r.json() : null))
      .then(datos => {
        if (!datos) return;
        // n8n suele responder envuelto en un array
        const limpio = sanear(Array.isArray(datos) ? datos[0] : datos);
        setTema(limpio);
        try {
          localStorage.setItem(CLAVE_CACHE, JSON.stringify(limpio));
        } catch { /* almacenamiento lleno o bloqueado: no es crítico */ }
      })
      .catch(() => { /* sin red o cortado por tiempo: se mantiene la caché */ })
      .finally(() => clearTimeout(corte));

    return () => {
      clearTimeout(corte);
      control.abort();
    };
  }, []);

  return tema;
}
